/**
 * Emotion-driven defaults for beat smoothing and dwell time.
 *
 * Every beat has four time knobs: `fade_sec` (body crossfade into the
 * beat), `hold_attack_sec` (procedural hold ramp-in), `hold_release_sec`
 * (procedural hold ramp-out), and `duration_sec` (total beat time).
 * Without emotion-aware defaults, every beat — sad, angry, joyful —
 * transitions at the same pace, leaving the *body's tempo* on the table
 * as a directorial signal.
 *
 * This module defines the per-emotion defaults. The materialiser
 * (`beat-utils.materialiseBeatFromInput`) consults `tempoForEmotion`
 * when a beat doesn't have an explicit override; what the user / LLM
 * specifies always wins. The UI uses the same function to compute
 * "auto: 1.4s" placeholders so the user can see what's coming through.
 *
 * KEEP IN SYNC with `server/src/emotion-tempo.ts` — both halves of the
 * stack consult the same table to produce identical Beats from the same
 * inputs. Byte-identical except for the import paths.
 *
 * ── Calibration ──────────────────────────────────────────────────────────
 *
 * Slow/heavy emotions (sadness, defeated, tired, bored, calm) lean into
 * long fades and long dwells — the body settles, gravity pulls. Reactive
 * emotions (fear, surprise, anger, tense, disgust) snap fast and dwell
 * briefly. Neutral sits at 1.6s/1.05s/1.0s/5s by design — a sensible
 * baseline that doesn't read as either heavy or hyper.
 *
 * The 0.5s floor was chosen empirically: sub-0.5s body crossfades read
 * as a teleport rather than a transition. Even fear's 0.7s feels like a
 * snap because the eyes/face hit the new emotion preset much faster.
 *
 * Each emotion also maps to an easing family (see `easing.ts`) — that's
 * how we get the *shape* of the curve to differ between, say, gentle
 * grief (ease-out quintic, very soft) and a panic jolt (ease-out expo,
 * near-instant attack with a slow tail). Fade durations alone can't
 * convey the difference; the curve does.
 */

import type { Emotion } from './vocab';
import type { EasingFamily } from '../motion/easing';

export interface EmotionTempo {
  /** Body crossfade duration into a beat with this emotion (seconds). */
  fade_sec: number;
  /** Procedural hold attack ramp duration (seconds). */
  hold_attack_sec: number;
  /** Procedural hold release ramp duration (seconds). */
  hold_release_sec: number;
  /** Default total beat time when the user/LLM doesn't override (seconds). */
  duration_sec: number;
  /** Easing family keying into the curve table in `easing.ts`. */
  easing: EasingFamily;
}

/**
 * Per-emotion defaults at intensity = 1.0 (full expression). Lower
 * intensities lerp toward the neutral baseline via `tempoForEmotion`.
 *
 * The 0.5s floor (see module doc) means the fastest emotion (surprise)
 * still has a 0.65s body crossfade — fast enough to read as a snap when
 * combined with the `jolt` easing curve, slow enough to avoid teleport
 * artefacts.
 */
export const EMOTION_TEMPO: Record<Emotion, EmotionTempo> = {
  // ── Slow / heavy / settling ─────────────────────────────────────────────
  sadness:    { fade_sec: 2.5, hold_attack_sec: 1.9,  hold_release_sec: 1.9, duration_sec: 12, easing: 'gentle' },
  defeated:   { fade_sec: 2.3, hold_attack_sec: 1.8,  hold_release_sec: 2.0, duration_sec: 14, easing: 'gentle' },
  tired:      { fade_sec: 2.1, hold_attack_sec: 1.7,  hold_release_sec: 1.9, duration_sec: 10, easing: 'gentle' },
  bored:      { fade_sec: 1.9, hold_attack_sec: 1.5,  hold_release_sec: 1.5, duration_sec: 8,  easing: 'gentle' },
  calm:       { fade_sec: 1.8, hold_attack_sec: 1.4,  hold_release_sec: 1.4, duration_sec: 8,  easing: 'gentle' },

  // ── Neutral / measured ───────────────────────────────────────────────────
  neutral:    { fade_sec: 1.6, hold_attack_sec: 1.05, hold_release_sec: 1.0, duration_sec: 5,  easing: 'smooth' },
  confused:   { fade_sec: 1.4, hold_attack_sec: 1.05, hold_release_sec: 1.0, duration_sec: 4,  easing: 'smooth' },

  // ── Engaged / positive ──────────────────────────────────────────────────
  joy:        { fade_sec: 1.2, hold_attack_sec: 0.95, hold_release_sec: 0.9, duration_sec: 4,  easing: 'smooth' },
  triumphant: { fade_sec: 1.1, hold_attack_sec: 0.9,  hold_release_sec: 0.9, duration_sec: 5,  easing: 'smooth' },
  disbelief:  { fade_sec: 1.0, hold_attack_sec: 0.9,  hold_release_sec: 0.9, duration_sec: 3,  easing: 'smooth' },
  excitement: { fade_sec: 1.0, hold_attack_sec: 0.85, hold_release_sec: 0.85, duration_sec: 3, easing: 'snap' },

  // ── Sharp / decisive ────────────────────────────────────────────────────
  disgust:    { fade_sec: 0.85, hold_attack_sec: 0.75, hold_release_sec: 0.9, duration_sec: 3, easing: 'snap' },
  tense:      { fade_sec: 0.85, hold_attack_sec: 0.7,  hold_release_sec: 0.9, duration_sec: 5, easing: 'snap' },
  anger:      { fade_sec: 0.8,  hold_attack_sec: 0.7,  hold_release_sec: 0.8, duration_sec: 4, easing: 'snap' },

  // ── Reactive / jolt ─────────────────────────────────────────────────────
  fear:       { fade_sec: 0.7,  hold_attack_sec: 0.65, hold_release_sec: 1.1, duration_sec: 3,   easing: 'jolt' },
  surprise:   { fade_sec: 0.65, hold_attack_sec: 0.6,  hold_release_sec: 1.0, duration_sec: 2.5, easing: 'jolt' },
};

/** Linear interpolate `a` → `b` by `t` (0..1). */
export function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/**
 * Resolve the effective tempo for a beat with the given emotion + intensity.
 *
 * Intensity lerps the per-emotion table values toward the neutral baseline:
 *   - intensity = 1.0  → full emotion-specific values (table hit unchanged)
 *   - intensity = 0.6  → mostly emotion, slightly toward neutral
 *   - intensity = 0.0  → fully collapsed onto neutral (subtle/muted)
 *
 * The easing family is NOT lerped (you can't half-snap, half-settle) —
 * it switches at intensity ≥ 0.5 so that subtle expressions still pick
 * up the emotion's curve shape, but a near-zero intensity falls back to
 * the smooth baseline.
 */
export function tempoForEmotion(
  emotion: Emotion,
  intensity = 1.0,
): EmotionTempo {
  const target = EMOTION_TEMPO[emotion];
  const base = EMOTION_TEMPO['neutral'];
  // Clamp intensity to [0,1] so we never extrapolate beyond the table.
  const t = Math.max(0, Math.min(1, intensity));
  return {
    fade_sec: lerp(base.fade_sec, target.fade_sec, t),
    hold_attack_sec: lerp(base.hold_attack_sec, target.hold_attack_sec, t),
    hold_release_sec: lerp(base.hold_release_sec, target.hold_release_sec, t),
    duration_sec: lerp(base.duration_sec, target.duration_sec, t),
    // Easing family: pick the emotion's curve only when the expression
    // is at half-strength or stronger. Below that, use the smooth baseline
    // so a flicker of an emotion doesn't carry its full curve signature.
    easing: t >= 0.5 ? target.easing : base.easing,
  };
}
